/**
 * @file LoginHistoryDialog.tsx
 * @brief Super-admin login-history modal — thin wrapper over the shared dialog.
 *
 * @details
 * Owns the query (`useUserLoginHistory` → GET /api/admin/users/:id/login-history)
 * and hands the result to the shared presentational `LoginHistoryDialog`.
 *
 * @author DES
 * @date 08-07-2026
 */

import { LoginHistoryDialog as SharedLoginHistoryDialog } from "@/shared/components/LoginHistoryDialog";
import { useUserLoginHistory } from "../api";
import type { AdminUser } from "../schemas";

interface LoginHistoryDialogProps {
  user: AdminUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LoginHistoryDialog({
  user,
  open,
  onOpenChange,
}: LoginHistoryDialogProps) {
  const { data, isLoading, isError } = useUserLoginHistory(
    user?.id ?? null,
    open,
  );

  const name =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    user?.email ||
    "user";

  return (
    <SharedLoginHistoryDialog
      name={name}
      open={open}
      onOpenChange={onOpenChange}
      history={data}
      isLoading={isLoading}
      isError={isError}
    />
  );
}
